import React, { createContext, useState, useEffect, useMemo } from "react";
import { HASURA_ENDPOINT_DEV } from "../config";
import { createClient, Provider, defaultExchanges } from "urql";
import "../firebase";
import { getAuth, onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { getFreshToken } from "../utils/authToken";
// import firebase from "firebase/compat/app";
// import { firebaseConfig } from "../config";

import { APP_BRANDING } from "../constants/branding";

export const AuthContext = createContext({});

// SECURITY (SECURITY_PLAN.md F27): never let the session token reach browser
// storage. `authData` is persisted only so the first render has a name/avatar
// and does not flash a signed-out UI; that is a display need, not a reason to
// store a credential. The real session lives in Firebase's own IndexedDB
// persistence, and onAuthStateChanged below re-fetches a fresh token on every
// load, so dropping `jwt` here costs nothing.
//
// Applied on BOTH read and write: stripping on write stops new tokens being
// stored, and stripping on read means a token already sitting in a returning
// user's localStorage is discarded instead of being loaded back into memory.
const stripToken = (data) => {
  if (!data || typeof data !== "object") return data;
  const { jwt, ...rest } = data;
  return rest;
};

const AuthProvider = ({ children }) => {
  const authc = getAuth();
  const [auth, setAuth] = useState(() => ({
    loading: true,
    data: stripToken(JSON.parse(localStorage.getItem("authData") || "null")),
  }));
  const [profile, setProfile] = useState({
    pLoading: true,
    data: JSON.parse(localStorage.getItem("currentProfileData") || "null"),
  });
  const matchedDomain = APP_BRANDING;

  useEffect(() => {
    document.title = APP_BRANDING.title;
    const metaDescription = document.querySelector("meta[name='description']");
    if (metaDescription) {
      metaDescription.setAttribute("content", APP_BRANDING.description);
    }

    const favicon =
      document.querySelector("link[rel='icon']") ||
      document.createElement("link");
    favicon.type = "image/x-icon";
    favicon.rel = "icon";
    favicon.href = APP_BRANDING.favicon;
    if (!document.querySelector("link[rel='icon']")) {
      document.head.appendChild(favicon);
    }
  }, []);

  // firebase.initializeApp(firebaseConfig);
  // No WebSocket client here on purpose. This app has no GraphQL
  // subscriptions, yet a SubscriptionClient used to be created on every
  // render: it connected eagerly with `Bearer undefined` before sign-in,
  // Hasura rejected it ("Could not verify JWT"), and `reconnect: true` retried
  // every few seconds forever, even in background tabs. About 22,000 rejected
  // connections an hour across customers on 2026-09-25. Queries and mutations
  // go over HTTP through `defaultExchanges`. If live updates are ever needed,
  // create the WebSocket client once, lazily, and only once a token exists.
  // Create the client once per token (PROJECT_STANDARDS rule 80, as
  // dashboard-admin-v2 does). It used to be rebuilt on every render of this
  // provider, so any state change here (switching profile, opening the
  // upgrade modal) handed the app a new client and every query on screen ran
  // again. A new token (sign-in, sign-out, the hourly renewal) still builds a
  // new client. The default policy stays network-only, so pages still fetch
  // fresh data when they open.
  //
  // The Authorization header is set per request from getFreshToken(), not
  // from the stored token: Firebase answers from memory while the token is
  // valid and renews it once it is about to expire, so a tab left open for
  // over an hour no longer sends an expired token (see utils/authToken.js).
  // The stored token is only the fallback, and it still decides whether a
  // header is sent at all, so signed-out pages stay anonymous.
  const jwt = auth?.data?.jwt;
  const client = useMemo(() => {
    const fetchWithFreshToken = async (input, init = {}) => {
      const token = jwt ? await getFreshToken(jwt) : null;
      const headers = { ...(init.headers || {}) };
      if (token) headers.Authorization = `Bearer ${token}`;
      return fetch(input, { ...init, headers });
    };
    return createClient({
      url: `https://${HASURA_ENDPOINT_DEV}`,
      exchanges: defaultExchanges,
      requestPolicy: "network-only",
      fetch: fetchWithFreshToken,
    });
  }, [jwt]);

  const setAuthData = (data) => {
    setAuth({ data: data });
  };

  const setProfileData = (data) => {
    setProfile({ pLoading: false, data: data });
  };

  useEffect(() => {
    document.cookie = `userEmail=${auth?.data?.email}; path=/`;
    // SECURITY (SECURITY_PLAN.md F27): persist the display fields only — never
    // `jwt`. See stripToken above.
    localStorage.setItem("authData", JSON.stringify(stripToken(auth.data)));
  }, [auth.data]);

  useEffect(() => {
    const ONE_MONTH_SECONDS = 60 * 60 * 24 * 30;

    document.cookie = "profile=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
    document.cookie =
      "hc_profile=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";

    if (profile?.data?.id) {
      document.cookie = `profile=${profile.data.id}; path=/`;
      document.cookie = `hc_profile=${profile.data.id}; path=/; max-age=${ONE_MONTH_SECONDS};`;
    }

    localStorage.setItem("currentProfileData", JSON.stringify(profile.data));
  }, [profile.data]);
  //  1. when **auth.data** changes we are setting **auth.data** in localStorage with the key 'authData'.

  useEffect(() => {
    return onAuthStateChanged(authc, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken(true);

          // SECURITY (SECURITY_PLAN.md F27): the session token used to be
          // written here as `userToken=<jwt>; path=/`. A cookie set from
          // JavaScript can never be HttpOnly, so any script on the page could
          // read the whole token from document.cookie — and it also carried no
          // Secure and no SameSite attribute.
          //
          // Safe to drop: every live caller takes the token from React state
          // (auth.data.jwt), never from this cookie. The one place that did
          // read it — extension-v3 Popup.jsx, via chrome.cookies — is dead
          // code: manifest.json registers no `default_popup`, clicking the
          // extension icon opens the dashboard instead (Background/index.js
          // chrome.action.onClicked), and the messages that popup sends
          // ('get-profile', 'connect-linkedin') have no handler. If the popup
          // is ever revived it must obtain a token some other way, NOT by
          // reintroducing this cookie.
          //
          // This deletion also clears the cookie already sitting in returning
          // users' browsers. It must repeat `path=/` — the old delete line
          // omitted it, so it never actually matched the cookie that was set
          // with a path, and the value survived.
          document.cookie =
            "userToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";

          // console.log(token);
          const idTokenResult = await user.getIdTokenResult();
          // console.log("id", idTokenResult);
          const hasuraClaim =
            idTokenResult.claims["https://hasura.io/jwt/claims"];
          // console.log("hasura", hasuraClaim);
          if (hasuraClaim["x-hasura-default-role"] === "user") {
            setAuthData({
              jwt: token,
              id: user.uid,
              verified: user.emailVerified,
              email: user.email,
              name: user?.displayName,
            });
          } else {
            setAuthData(null);
          }
        } catch (error) {}
      } else {
        setAuthData(null);
      }
    });
  }, []);

  // Keep the token fresh. Firebase ID tokens expire after one hour, and the
  // listener above only runs on sign-in and sign-out, so `auth.data.jwt` used
  // to go stale while a tab stayed open and every call then failed; the
  // 10-minute idle reload in SidebarContent.js was the workaround.
  // onIdTokenChanged fires whenever the token is renewed. Correction
  // 2026-10-02: the SDK does not renew by itself in this app (it only does
  // so when another Firebase product such as Firestore is running), so this
  // listener alone never fixed an idle tab. The renewal now happens when a
  // request asks for the token through getFreshToken() (utils/authToken.js),
  // and this listener then stores the new token.
  //
  // Deliberately narrow: it only swaps a token we already hold, for the same
  // user. Sign-in, the role check and sign-out stay in the listener above,
  // whose forced refresh picks up the Hasura claims added after signup.
  // Never call getIdToken(true) here: a forced refresh fires this listener
  // again and would loop.
  useEffect(() => {
    return onIdTokenChanged(authc, async (user) => {
      if (!user) return;
      try {
        const token = await user.getIdToken();
        setAuth((prev) => {
          if (!prev?.data?.jwt || prev.data.id !== user.uid) return prev;
          if (prev.data.jwt === token) return prev;
          return { ...prev, data: { ...prev.data, jwt: token } };
        });
      } catch (error) {}
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        auth,
        profile,
        setAuthData,
        setProfileData,
        matchedDomain,
      }}
    >
      <Provider value={client}>{children}</Provider>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
